/**
 * Prioridade da home tenant — “o que precisa de mim agora?”.
 * Por quê: atalho estático não escala; a decisão muda com setup, cota e fila.
 */

import type { Campaign } from "../../helpers/campaigns_helper";
import type { CampaignSend } from "../../helpers/sends_helper";
import type { SendingSetup } from "../../helpers/sending_setup_helper";
import type { InactiveContactsSummary } from "../../helpers/audience_helper";

export type AttentionTone = "warning" | "danger" | "info";

export type AttentionItem = {
  id: string;
  tone: AttentionTone;
  titleKey: string;
  bodyKey: string;
  bodyValues?: Record<string, string | number>;
  ctaKey: string;
  to: string;
  dismissible?: boolean;
};

export type NextStep = {
  id: string;
  tone: "primary" | "warning" | "info" | "success";
  titleKey: string;
  bodyKey: string;
  bodyValues?: Record<string, string | number>;
  ctaKey: string;
  to: string;
  secondaryCtaKey?: string;
  secondaryTo?: string;
};

export type DiscoveryCard = {
  id: string;
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  to: string;
  icon: string;
};

export type NextStepInput = {
  setup: SendingSetup | null;
  setupLoading: boolean;
  pendingApprovalCount: number;
  canApprove: boolean;
  quotaExhausted: boolean;
  quotaNearLimit: boolean;
  showDailyCap: boolean;
  dailyRemaining: number;
  dailyCapResetLocal: string;
  prepareSendHref: string;
  inactiveSummary: InactiveContactsSummary | null;
  apiFailedToday: number;
  /** Token com mais falhas hoje — copy do alerta. */
  apiFailedTopTokenName?: string | null;
  apiFailedTopTokenId?: number | null;
  campaignsTotal: number | null;
  campaignWithoutHtml: Campaign | null;
  recentSends: CampaignSend[];
  mfaNeeded: boolean;
  /** Taxas 7d já formatadas (ex.: "17,63%") — strip Atenção no topo. */
  bounceRateLabel?: string | null;
  hardBounceRateLabel?: string | null;
  bounceRateHigh?: boolean;
  hardBounceRateHigh?: boolean;
};

/** Chave i18n do próximo passo de setup (mesmo mapa do banner antigo). */
export function setupNextStepKey(setup: SendingSetup): string {
  if (!setup.tracking_hostname) {
    return "dashboard.setup.next.start";
  }
  if (setup.tracking_status === "pending") {
    return "dashboard.setup.next.tracking";
  }
  if (setup.tracking_status === "dns_ok") {
    return "dashboard.setup.next.ssl";
  }
  if (setup.tracking_status === "ssl_ok") {
    return "dashboard.setup.next.activating";
  }
  if (setup.ses_identity_status !== "verified") {
    return "dashboard.setup.next.sender";
  }
  return "dashboard.setup.next.tracking";
}

/**
 * Uma ação principal — ordem = urgência operacional.
 */
export function resolveNextStep(input: NextStepInput): NextStep {
  const {
    setup,
    setupLoading,
    pendingApprovalCount,
    canApprove,
    quotaExhausted,
    quotaNearLimit,
    showDailyCap,
    dailyRemaining,
    dailyCapResetLocal,
    prepareSendHref,
    campaignsTotal,
    campaignWithoutHtml,
    recentSends,
  } = input;

  if (!setupLoading && setup && !setup.is_ready) {
    return {
      id: "setup",
      tone: "warning",
      titleKey: "dashboard.nextStep.setup.title",
      bodyKey: setupNextStepKey(setup),
      ctaKey: "dashboard.setup.cta",
      to: "/settings/sending",
    };
  }

  if (canApprove && pendingApprovalCount > 0) {
    return {
      id: "approval",
      tone: "warning",
      titleKey: "dashboard.nextStep.approval.title",
      bodyKey: "dashboard.nextStep.approval.body",
      bodyValues: { count: pendingApprovalCount },
      ctaKey: "dashboard.nextStep.approval.cta",
      to: "/sends?pending=mine",
    };
  }

  const paused = recentSends.find((s) => s.status === "paused");
  if (paused) {
    return {
      id: "paused",
      tone: "warning",
      titleKey: "dashboard.nextStep.paused.title",
      bodyKey: "dashboard.nextStep.paused.body",
      ctaKey: "dashboard.nextStep.paused.cta",
      to: `/sends/${paused.id}`,
    };
  }

  if (quotaExhausted) {
    return {
      id: "quota",
      tone: "warning",
      titleKey: "dashboard.nextStep.quota.title",
      bodyKey: "dashboard.nextStep.quota.body",
      ctaKey: "dashboard.quota.upgradeCta",
      to: "/settings/subscription",
    };
  }

  if (showDailyCap && dailyRemaining <= 0) {
    return {
      id: "dailyCap",
      tone: "warning",
      titleKey: "dashboard.nextStep.dailyCap.title",
      bodyKey: "dashboard.dailyCap.exhausted",
      bodyValues: { resetAt: dailyCapResetLocal },
      ctaKey: "dashboard.dailyCap.exhaustedSendCta",
      to: prepareSendHref,
      secondaryCtaKey: "dashboard.dailyCap.exhaustedCta",
      secondaryTo: "/campaigns",
    };
  }

  if (campaignsTotal === 0) {
    return {
      id: "firstCampaign",
      tone: "primary",
      titleKey: "dashboard.nextStep.firstCampaign.title",
      bodyKey: "dashboard.nextStep.firstCampaign.body",
      ctaKey: "dashboard.nextStep.firstCampaign.cta",
      to: "/campaigns/new",
      secondaryCtaKey: "dashboard.nextStep.firstCampaign.gallery",
      secondaryTo: "/templates/gallery",
    };
  }

  if (campaignWithoutHtml) {
    return {
      id: "html",
      tone: "info",
      titleKey: "dashboard.nextStep.html.title",
      bodyKey: "dashboard.nextStep.html.body",
      bodyValues: { name: campaignWithoutHtml.name },
      ctaKey: "dashboard.nextStep.html.cta",
      to: `/campaigns/${campaignWithoutHtml.id}/edit`,
    };
  }

  if (recentSends.length === 0 && campaignsTotal != null && campaignsTotal > 0) {
    return {
      id: "firstSend",
      tone: "primary",
      titleKey: "dashboard.nextStep.firstSend.title",
      bodyKey: "dashboard.nextStep.firstSend.body",
      ctaKey: "dashboard.nextStep.firstSend.cta",
      to: prepareSendHref,
      secondaryCtaKey: "dashboard.cta.campaigns",
      secondaryTo: "/campaigns",
    };
  }

  if (quotaNearLimit) {
    return {
      id: "nearQuota",
      tone: "info",
      titleKey: "dashboard.nextStep.nearQuota.title",
      bodyKey: "dashboard.nextStep.nearQuota.body",
      ctaKey: "dashboard.quota.viewPlan",
      to: "/settings/subscription",
      secondaryCtaKey: "dashboard.nextStep.ready.cta",
      secondaryTo: prepareSendHref,
    };
  }

  const last = recentSends[0];
  if (last) {
    return {
      id: "ready",
      tone: "success",
      titleKey: "dashboard.nextStep.ready.title",
      bodyKey: "dashboard.nextStep.ready.body",
      ctaKey: "dashboard.nextStep.ready.cta",
      to: prepareSendHref,
      secondaryCtaKey: "dashboard.nextStep.ready.secondary",
      secondaryTo: `/sends/${last.id}`,
    };
  }

  return {
    id: "readyEmpty",
    tone: "success",
    titleKey: "dashboard.nextStep.ready.title",
    bodyKey: "dashboard.nextStep.readyEmpty.body",
    ctaKey: "dashboard.nextStep.ready.cta",
    to: prepareSendHref,
  };
}

/** Itens secundários do strip (o next-step já cobriu a urgência principal). */
export function buildAttentionItems(
  input: NextStepInput,
  nextStepId: string
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const threshold =
    input.inactiveSummary?.dashboard_threshold_percent ?? 20;

  // Setup / aprovação / cota / teto já são o next-step — não duplicar no strip.
  if (
    nextStepId !== "setup" &&
    input.setup &&
    !input.setup.is_ready &&
    !input.setupLoading
  ) {
    items.push({
      id: "setup",
      tone: "warning",
      titleKey: "dashboard.setup.title",
      bodyKey: setupNextStepKey(input.setup),
      ctaKey: "dashboard.setup.cta",
      to: "/settings/sending",
    });
  }

  if (
    nextStepId !== "approval" &&
    input.canApprove &&
    input.pendingApprovalCount > 0
  ) {
    items.push({
      id: "approval",
      tone: "warning",
      titleKey: "dashboard.nextStep.approval.title",
      bodyKey: "dashboard.nextStep.approval.body",
      bodyValues: { count: input.pendingApprovalCount },
      ctaKey: "dashboard.nextStep.approval.cta",
      to: "/sends?pending=mine",
    });
  }

  if (input.mfaNeeded) {
    items.push({
      id: "mfa",
      tone: "info",
      titleKey: "dashboard.mfa.title",
      bodyKey: "dashboard.mfa.body",
      ctaKey: "dashboard.mfa.cta",
      to: "/settings/account",
      dismissible: true,
    });
  }

  if (
    input.inactiveSummary &&
    input.inactiveSummary.eligible_percent >= threshold
  ) {
    items.push({
      id: "inactive",
      tone: "info",
      titleKey: "dashboard.inactiveList.title",
      bodyKey: "dashboard.inactiveList.body",
      bodyValues: {
        percent: input.inactiveSummary.eligible_percent,
        days: input.inactiveSummary.days,
        count: input.inactiveSummary.eligible_count,
      },
      ctaKey: "dashboard.inactiveList.cta",
      to: "/contacts/inactive",
    });
  }

  if (input.apiFailedToday > 0) {
    const tokenId = input.apiFailedTopTokenId;
    const tokenName = input.apiFailedTopTokenName;
    items.push({
      id: "api",
      tone: "danger",
      titleKey: "dashboard.apiFailures.title",
      bodyKey: tokenName
        ? "dashboard.apiFailures.bodyWithToken"
        : "dashboard.apiFailures.body",
      bodyValues: {
        count: input.apiFailedToday,
        token: tokenName || "",
      },
      ctaKey: "dashboard.apiFailures.cta",
      to:
        tokenId && tokenId > 0
          ? `/settings/api-tokens?api_messages=failed&api_token_id=${tokenId}`
          : "/settings/api-tokens?api_messages=failed",
    });
  }

  // Bounce alto (≥ limiar da conta, default 5%): no topo (Atenção).
  if (input.hardBounceRateHigh && input.hardBounceRateLabel) {
    items.push({
      id: "hardBounce",
      tone: "danger",
      titleKey: "dashboard.attention.hardBounce.title",
      bodyKey: "dashboard.attention.hardBounce.body",
      bodyValues: { rate: input.hardBounceRateLabel },
      ctaKey: "dashboard.attention.hardBounce.cta",
      to: "/contacts?hard_bounce_flagged=1",
    });
  } else if (input.bounceRateHigh && input.bounceRateLabel) {
    // Preferir último envio com bounce filtrado; senão import (higiene da lista).
    const lastSendId = input.recentSends.find((s) => !s.is_test)?.id;
    items.push({
      id: "bounce",
      tone: "danger",
      titleKey: "dashboard.attention.bounce.title",
      bodyKey: "dashboard.attention.bounce.body",
      bodyValues: { rate: input.bounceRateLabel },
      ctaKey: lastSendId
        ? "dashboard.attention.bounce.ctaSend"
        : "dashboard.attention.bounce.ctaImport",
      to: lastSendId
        ? `/sends/${lastSendId}?status=bounced`
        : "/contacts/import",
    });
  }

  if (
    nextStepId !== "dailyCap" &&
    input.showDailyCap &&
    input.dailyRemaining <= 0
  ) {
    items.push({
      id: "dailyCap",
      tone: "warning",
      titleKey: "dashboard.nextStep.dailyCap.title",
      bodyKey: "dashboard.dailyCap.exhausted",
      bodyValues: { resetAt: input.dailyCapResetLocal },
      ctaKey: "dashboard.dailyCap.exhaustedSendCta",
      to: input.prepareSendHref,
    });
  }

  return sortAttentionBySeverity(items);
}

/** danger → warning → info (Carla vê o urgente primeiro). */
export function sortAttentionBySeverity(
  items: AttentionItem[]
): AttentionItem[] {
  const rank: Record<AttentionTone, number> = {
    danger: 0,
    warning: 1,
    info: 2,
  };
  return [...items].sort((a, b) => rank[a.tone] - rank[b.tone]);
}

/** Até 2 cards F2 — só empty states (descoberta sem App Store). */
export function buildDiscoveryCards(opts: {
  segmentsTotal: number | null;
  formsTotal: number | null;
  welcomeEnabled: boolean | null;
  /** Win-back off + lista fria alta → sugerir reengajamento. */
  winBackOffAndCold?: boolean;
}): DiscoveryCard[] {
  const cards: DiscoveryCard[] = [];

  if (opts.winBackOffAndCold) {
    cards.push({
      id: "winback",
      titleKey: "dashboard.discover.winback.title",
      bodyKey: "dashboard.discover.winback.body",
      ctaKey: "dashboard.discover.winback.cta",
      to: "/automations/win-back",
      icon: "ri-refresh-line",
    });
  }

  if (opts.segmentsTotal === 0) {
    cards.push({
      id: "segments",
      titleKey: "dashboard.discover.segments.title",
      bodyKey: "dashboard.discover.segments.body",
      ctaKey: "dashboard.discover.segments.cta",
      to: "/segments",
      icon: "ri-filter-3-line",
    });
  }

  if (opts.welcomeEnabled === false && cards.length < 2) {
    cards.push({
      id: "welcome",
      titleKey: "dashboard.discover.welcome.title",
      bodyKey: "dashboard.discover.welcome.body",
      ctaKey: "dashboard.discover.welcome.cta",
      to: "/automations/welcome",
      icon: "ri-hand-heart-line",
    });
  }

  if (opts.formsTotal === 0 && cards.length < 2) {
    cards.push({
      id: "forms",
      titleKey: "dashboard.discover.forms.title",
      bodyKey: "dashboard.discover.forms.body",
      ctaKey: "dashboard.discover.forms.cta",
      to: "/forms",
      icon: "ri-survey-line",
    });
  }

  return cards.slice(0, 2);
}

/** Relativo curto (“há 3 h”) — sem dependência de date-fns. */
export function formatRelativePast(
  iso: string | null | undefined,
  locale: string,
  nowMs = Date.now()
): string | null {
  if (!iso) {
    return null;
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return null;
  }
  const diffSec = Math.round((then - nowMs) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale || "pt-BR", {
    numeric: "auto",
  });
  if (abs < 60) {
    return rtf.format(diffSec, "second");
  }
  if (abs < 3600) {
    return rtf.format(Math.round(diffSec / 60), "minute");
  }
  if (abs < 86400) {
    return rtf.format(Math.round(diffSec / 3600), "hour");
  }
  if (abs < 86400 * 30) {
    return rtf.format(Math.round(diffSec / 86400), "day");
  }
  return rtf.format(Math.round(diffSec / (86400 * 30)), "month");
}

export function sendStatusBadgeClass(status: CampaignSend["status"]): string {
  switch (status) {
    case "completed":
      return "bg-success-subtle text-success";
    case "sending":
    case "preparing":
    case "queued":
      return "bg-info-subtle text-info";
    case "failed":
    case "rejected":
      return "bg-danger-subtle text-danger";
    case "pending_approval":
    case "paused":
    case "cancelled":
      return "bg-warning-subtle text-warning";
    default:
      return "bg-secondary-subtle text-secondary";
  }
}
